import React, { useState, useEffect } from 'react';
import { ORG_LINE1, ORG_LINE2, PHOTO_PATH } from '../constants';

const STATUS_STEPS = [
  { value: 'Registration Done', labelEn: 'Registration Done', labelMr: 'नोंदणी पूर्ण झाली' },
  { value: 'Card Dispatched to Vendor', labelEn: 'Card Dispatched to Vendor', labelMr: 'कार्ड विक्रेत्याकडे पाठवले' },
  { value: 'Card Dispatched to DC', labelEn: 'Card Dispatched to DC', labelMr: 'कार्ड जिल्हा समन्वयाकाकडे पाठवले' },
  { value: 'Card Dispatched to TCC', labelEn: 'Card Dispatched to TCC', labelMr: 'कार्ड टीसीसीकडे पाठवले' },
  { value: 'Card Dispatched to Survivor', labelEn: 'Card Dispatched to Survivor', labelMr: 'कार्ड लाभार्थ्याकडे पाठवले' },
  { value: 'Card Delivered', labelEn: 'Card Delivered', labelMr: 'कार्ड वितरित झाले' }
];

function VerificationPage({ aadhaar, API }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!aadhaar) {
      setError('Aadhaar number is missing / आधार क्रमांक गहाळ आहे');
      setLoading(false);
      return;
    }

    fetch(`${API}/registration/verify?aadhaar=${aadhaar}`)
      .then((res) => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('Registration not found / नोंदणी आढळली नाही');
          }
          throw new Error('Verification failed / पडताळणी अयशस्वी');
        }
        return res.json();
      })
      .then((json) => {
        setData(json);
      })
      .catch((err) => {
        setError(err.message || 'Error connecting to server / सर्व्हरशी कनेक्ट करताना त्रुटी');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [aadhaar, API]);

  const getCurrentStepIndex = () => {
    if (!data) return -1;
    const currentStatus = data.status || 'Registration Done';
    return STATUS_STEPS.findIndex((step) => step.value === currentStatus);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div className="verification-page">
      <div className="verification-card">
        <div className="verification-header-accent">
          <span className="secure-badge">🔒 SECURE VERIFICATION PORTAL</span>
        </div>

        {loading && (
          <div className="verify-loading">
            <div className="verify-spinner"></div>
            <p>Verifying details / तपशील पडताळत आहे...</p>
          </div>
        )}

        {error && (
          <div className="verify-error-card">
            <div className="error-icon-big">❌</div>
            <h3>Verification Failed / पडताळणी अयशस्वी</h3>
            <p>{error}</p>
            <button className="btn-primary" onClick={() => window.location.href = '/'}>
              Go to Home / मुख्यपृष्ठावर जा
            </button>
          </div>
        )}

        {data && (
          <div className="verify-content">
            <div className="verify-success-header">
              <div className="success-badge-large">✓ VERIFIED BENEFICIARY</div>
              <h2>{data.kamgarName}</h2>
              <p className="subtitle-mr">नोंदणीकृत कामगार तपशील</p>
            </div>

            <div className="verify-grid">
              <div className="verify-details-section">
                <h3>Card Details / कार्ड तपशील</h3>
                <table className="verify-details-table">
                  <tbody>
                    <tr>
                      <td className="lbl">Kutumb Kramank<br/>(कुटुंब क्रमांक)</td>
                      <td className="val">{data.kutumbKramank}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Sadasya Kramank<br/>(सदस्य क्रमांक)</td>
                      <td className="val">{data.sadasyaKramank}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Aadhaar Number<br/>(आधार क्रमांक)</td>
                      <td className="val">xxxx-xxxx-{data.aadhaarNumber.slice(-4)}</td>
                    </tr>
                    <tr>
                      <td className="lbl">District<br/>(जिल्हा)</td>
                      <td className="val">{data.district}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Taluka<br/>(तालुका)</td>
                      <td className="val">{data.taluka}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Village<br/>(गाव)</td>
                      <td className="val">{data.village}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Mobile Number<br/>(मोबाईल क्रमांक)</td>
                      <td className="val">xxxxxx{data.mobile.slice(-4)}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Arj Kramank<br/>(अर्ज क्रमांक)</td>
                      <td className="val">{data.arjKramank}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Kamgar Prakar<br/>(कामगार प्रकार)</td>
                      <td className="val">{data.kamgarPrakar}</td>
                    </tr>
                    <tr>
                      <td className="lbl">Registration Date<br/>(नोंदणी तारीख)</td>
                      <td className="val">{data.timestamp.split(',')[0]}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="verify-stepper-section">
                <h3>Card Journey / कार्डचा प्रवास</h3>
                <div className="stepper-timeline">
                  {STATUS_STEPS.map((step, idx) => {
                    const isCompleted = idx <= currentStepIndex;
                    const isActive = idx === currentStepIndex;
                    return (
                      <div
                        key={step.value}
                        className={`stepper-item ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                      >
                        <div className="stepper-node-wrapper">
                          <div className="stepper-node">
                            {isCompleted && !isActive ? '✓' : idx + 1}
                          </div>
                          {idx < STATUS_STEPS.length - 1 && <div className="stepper-line"></div>}
                        </div>
                        <div className="stepper-label">
                          <div className="label-en">{step.labelEn}</div>
                          <div className="label-mr">{step.labelMr}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="verify-actions">
              <button className="btn-secondary" onClick={() => window.location.href = '/'}>
                Back to Registration Portal / मुख्यपृष्ठावर जा
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VerificationPage;
